import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Divider, Tooltip, TableCell } from "@mui/material";
import { useAccessControl } from "../../utils/hooks/useAccessControl";
import { DUMMY_SCHEDULES } from "../../utils/data/controlData";

import {
  SheetsDirective,
  SheetDirective,
  RangesDirective,
  RangeDirective,
  SpreadsheetComponent,
} from "@syncfusion/ej2-react-spreadsheet";
import "@syncfusion/ej2-base/styles/material.css";
import "@syncfusion/ej2-buttons/styles/material.css";
import "@syncfusion/ej2-calendars/styles/material.css";
import "@syncfusion/ej2-dropdowns/styles/material.css";
import "@syncfusion/ej2-inputs/styles/material.css";
import "@syncfusion/ej2-navigations/styles/material.css";
import "@syncfusion/ej2-popups/styles/material.css";
import "@syncfusion/ej2-react-spreadsheet/styles/material.css";
import "@syncfusion/ej2-icons/styles/material.css";
import "@syncfusion/ej2-splitbuttons/styles/material.css";
import { registerLicense } from "@syncfusion/ej2-base";
import {
  serializeData,
  deserializeData,
  formatHistoryAmount,
  formatAmount,
  isAutoload,
} from "../../utils/hooks/dataHandler";

import { AuthContext } from "../../contexts/AuthContext";

import ScheduleHeader from "../../components/App/ScheduleHeader";
import PdfGenerator from "../../components/Schedules/Others/Pdf/SavyPdf";

import {
  autoload_section_groups,
  autoload_section_groups_investment,
  savy_tax_adjustment_options,
} from "../../utils/static/MainTaxComputation";
import CommonTable from "../../components/utils/CommonTable";
import { CalculatorTextField } from "../../components/utils/TextField/NumericTextField";
import {
  TextFieldForName,
  TextFieldForViewOnly,
} from "../../components/utils/TextField/TextField";
import { useErrorDialog } from "../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../contexts/SuccessDialogContext";
import ComponentLoading from "../../components/App/ComponentLoading";
import ScheduleNotes from "../../components/Schedules/ScheduleNotes";
import IconButtonWithTooltip from "../../components/utils/IconButtonWithTooltip";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ScheduleContainer from "../../components/Schedules/utils/ScheduleContainer";
import SectionBox from "../../components/Schedules/utils/SectionBox";
import CustomDialog from "../../components/utils/dialog/customDialog";
import SavyExcelUpload from "../../components/Schedules/Savy/SavyExcelUpload";

registerLicense(process.env.REACT_APP_SPREAD_SHEET_LICENSE);

const DummySchedule = ({ company, scheduleId }) => {
  const spreadsheetRef = useRef(null);
  const { updatePLSchedule, getScheduleData, deleteScheduleByScheduleType } =
    useContext(AuthContext);

  const { canEdit: canEditDummySchedule } = useAccessControl(DUMMY_SCHEDULES);

  const [scheduleData, setScheduleData] = useState({
    scheduleTitle: "",
    scheduleNo: 1,
    rows: [],
    comments: "",
    reviewNotes: "",
    taxAdjustment: "Added Back",
    section: "Tax Adjustments",
    type: "Savy",
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false); // Track if data has been loaded

  const [spreadSheetData, setSpreadSheetData] = useState(null);
  const [initSpreadSheetData, setInitSpreadSheetData] = useState([]);

  // Excel upload dialog state
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const handleOpenExcelUploadDialog = () => setExcelDialogOpen(true);
  const handleCloseExcelUploadDialog = () => setExcelDialogOpen(false);

  const location = useLocation();
  const states = location.state || {};

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  // Fetch data from server when component mounts
  useEffect(() => {
    const fetchData = async () => {
      if (scheduleId) {
        const response = await getScheduleData(scheduleId, "Savy");
        const data = response?.schedule;
        if (data) {
          setScheduleData({
            scheduleTitle: data.scheduleTitle,
            scheduleNo: data.scheduleNo,
            type: "Savy",
            rows: [
              ...(data.rows || []).map((row) => {
                if (!row.historyAmount || row.historyAmount.length <= 0) {
                  row.historyAmount = [row.amount];
                }
                return row;
              }),
            ],
            comments: data.comments,
            reviewNotes: data.reviewNotes,
            taxAdjustment: data.taxAdjustment || "",
            section: data.section || "Tax Adjustments",
          });

          if (data.spreadSheetData) {
            setInitSpreadSheetData(data.spreadSheetData);
          }

          setDataLoaded(true);
        }
        setDataLoaded(true);
      }
    };
    fetchData();
  }, [scheduleId, getScheduleData, company.uen]);

  const loadData = () => {
    try {
      const spreadsheet = spreadsheetRef.current;

      if (!initSpreadSheetData) {
        console.warn("initSpreadSheetData is empty or invalid.");
        if (spreadsheet) {
          spreadsheet.clear({ sheetIndex: 0 });
        }
        return;
      }

      const { name, data, columnWidths } = initSpreadSheetData;

      if (!name) {
        console.error("Sheet name is invalid or empty.");
        return;
      }

      // Validate and deserialize the data
      const deserializedData = deserializeData(data);
      if (!deserializedData || !(deserializedData instanceof Map)) {
        console.warn("Deserialized data is invalid or empty.");
        if (spreadsheet) {
          spreadsheet.clear({ sheetIndex: 0 });
        }
        return;
      }

      // Validate columnWidths
      if (!Array.isArray(columnWidths)) {
        console.error("Invalid columnWidths format:", columnWidths);
        return;
      }

      if (spreadsheet) {
        // Set the sheet name dynamically
        spreadsheet.sheets[0].name = name || "Sheet1";

        // Load cell data
        deserializedData.forEach((cellData, cellKey) => {
          if (cellData.formula) {
            spreadsheet.updateCell(
              {
                formula: cellData.formula,
                style: cellData.style || {},
                colSpan: cellData.colSpan || 1,
              },
              `${name}!${cellKey}`
            );
          } else {
            spreadsheet.updateCell(
              {
                value: cellData.value,
                style: cellData.style || {},
                colSpan: cellData.colSpan || 1,
              },
              `${name}!${cellKey}`
            );
          }
        });

        // Apply column widths
        columnWidths.forEach((col) => {
          if (
            col &&
            col.index !== undefined &&
            col.width !== undefined &&
            col.width > 0
          ) {
            spreadsheet.setColWidth(col.width, col.index);
          } else {
            console.warn(
              `Skipping invalid column width data: ${JSON.stringify(col)}`
            );
          }
        });
      }
    } catch (error) {
      console.error(
        "An error occurred while loading data into the spreadsheet:",
        error
      );
      const spreadsheet = spreadsheetRef.current;
      if (spreadsheet) {
        spreadsheet.clear({ sheetIndex: 0 });
      }
    }
  };

  // Add a new row manually
  const handleAddRow = () => {
    setScheduleData((prevState) => ({
      ...prevState,
      rows: [...prevState.rows, { name: "", amount: "" }],
    }));
  };

  const onExcelAddRows = (newRows) => {
    if (!Array.isArray(newRows) || newRows.length === 0) return;

    const rowsToAdd = newRows.map((row) => ({
      name: row.name || "",
      amount: row.amount || 0,
      historyAmount: row.amount ? [row.amount] : [],
      reference: row.reference || "",
    }));

    setScheduleData((prevState) => {
      const updatedRows = [...prevState.rows, ...rowsToAdd];

      return { ...prevState, rows: updatedRows };
    });

    handleCloseExcelUploadDialog();
  };

  // Remove a row
  const handleRemoveRow = (index) => {
    setScheduleData((prevState) => ({
      ...prevState,
      rows: prevState.rows.filter((_, i) => i !== index),
    }));
  };

  // Handle field changes in the rows
  const handleChange = (index, field, value) => {
    const updatedRows = scheduleData.rows.map((row, i) => {
      if (i === index) {
        if (field === "amount") {
          return {
            ...row,
            [field]: value.reduce((sum, num) => sum + num, 0),
            historyAmount: value,
          };
        } else return { ...row, [field]: value };
      } else return row;
    });
    setScheduleData((prevState) => ({
      ...prevState,
      rows: updatedRows,
    }));
  };

  // Handle change for other schedule data fields
  const handleFieldChange = (field, value) => {
    setScheduleData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  // Calculate the total amount
  const calculateTotalAmount = () => {
    return scheduleData.rows
      .reduce((sum, row) => sum + parseFloat(row.amount || 0), 0)
      .toFixed(2);
  };

  const handleSpreadSheetPrint = () => {
    let spreadsheet = spreadsheetRef.current;
    if (spreadsheet) {
      spreadsheet.print({
        type: "Workbook",
        allowGridLines: false,
        allowRowColumnHeader: false,
      });
    }
  };

  const deleteSchedule = async () => {
    if (!scheduleId) {
      handleError("Missing Schedule ID.");
      return;
    }

    const data = {
      sch_id: scheduleId,
    };

    setLoading(true);
    const response = await deleteScheduleByScheduleType(data);
    setLoading(false);

    if (response) {
      handleSuccess("Schedule Deleted successfully.");
      localStorage.removeItem("selectedSection");
      localStorage.setItem("selectedComponent", "Detailed P&L");
      window.location.reload();
    } else {
      handleError("Error delete schedule.");
    }
  };

  // const handleSave = () => {
  //   if (spreadsheetRef.current) {
  //     const spreadsheet = spreadsheetRef.current;

  //     // Get the sheet instance
  //     const sheet = spreadsheet.sheets[0];
  //     const sheetName = sheet.name;

  //     // Get data and column widths
  //     spreadsheet.getData(`${sheetName}!A1:Z100`).then((result) => {
  //       const dataObject = Object.fromEntries(result);
  //       const compactString = serializeData(dataObject);

  //       // Capture specific widths for each column
  //       const columnWidths = sheet.columns.map((col, index) => ({
  //         index,
  //         width: col.width || 0, // Fallback to 0 if width is not defined
  //       }));

  //       // Combine spreadsheet data with column widths
  //       const savedData = {
  //         name: sheetName,
  //         data: compactString,
  //         columnWidths,
  //       };

  //       setSpreadSheetData(savedData);
  //       setDialogOpen(true);
  //     });
  //   }
  // };

  const handleSave = async () => {
    setLoading(true);
    if (!company.uen || !scheduleId) {
      handleError("Missing UEN or Savy Schedule ID.");
      setDialogOpen(false);
      return;
    }

    const updatedDummySchedule = {
      sch_id: scheduleId,
      scheduleTitle: scheduleData.scheduleTitle,
      scheduleType: "Savy",
      comments: scheduleData.comments,
      reviewNotes: scheduleData.reviewNotes,
      scheduleNo: scheduleData.scheduleNo,
      section: scheduleData.section,
      taxAdjustment: scheduleData.taxAdjustment,
      rows: scheduleData.rows,
      totalAmount: calculateTotalAmount(),
      yearOfAssessment: company.yearOfAssessment,
      financialYearEnd: company.financialYearEnd,
      spreadSheetData: spreadSheetData,
      doc: JSON.stringify({
        taxAdjustment: scheduleData.taxAdjustment,
        scheduleData: scheduleData.rows,
        notes: scheduleData.comments,
        reviewNotes: scheduleData.reviewNotes,
      }),
    };

    try {
      const response = await updatePLSchedule(updatedDummySchedule);

      if (response) {
        handleSuccess("The schedule has been saved successfully.");
        //navigate("/main-doc", { state: mergedState });
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
    // setDialogOpen(false);
  };

  // Render only if data is loaded
  if (!dataLoaded) {
    return <ComponentLoading />;
  }

  if (loading) {
    return <ComponentLoading />;
  }

  return (
    <Box sx={{ padding: 2 }}>
      <ScheduleHeader
        {...{
          title: "Savy Schedule",
          companyName: company.name,
          uen: company.uen,
          companyType: company.companyType,
          yearOfAssessment: company.yearOfAssessment,
          financialYearStart: company.financialYearStart,
          financialYearEnd: company.financialYearEnd,
          // handleSpreadSheetPrint,
          handleSave,
          deleteSchedule,
          handleExcel: handleOpenExcelUploadDialog,
          canEditSchedule: canEditDummySchedule,
        }}
      >
        <PdfGenerator
          company={company}
          schedule={{
            name: scheduleData.scheduleTitle,
            scheduleNo: scheduleData.scheduleNo,
            taxAdjustment: scheduleData.taxAdjustment,
          }}
          scheduleData={scheduleData.rows}
          notes={scheduleData.comments}
          reviewNotes={scheduleData.reviewNotes}
        />
      </ScheduleHeader>
      <br />

      <ScheduleContainer
        key={scheduleId}
        id={scheduleId}
        schedule={scheduleData}
        schName={scheduleData.scheduleTitle}
        taxAdjustment={scheduleData.taxAdjustment}
        section={scheduleData.section}
        allowedToEditSchName
        allowedToAdjustTax
        allowedToEditSection
        taxAdjustOptions={savy_tax_adjustment_options}
        sectionOptions={
          company.companyType === "investment"
            ? autoload_section_groups_investment
            : autoload_section_groups
        }
        updateHeading={(newTitle) => {
          handleFieldChange("scheduleTitle", newTitle);
        }}
        updateAdjustment={(newAdjustment) => {
          handleFieldChange("taxAdjustment", newAdjustment);
        }}
        updateSection={(newSection) => {
          handleFieldChange("section", newSection);
        }}
        canEditSchedule={canEditDummySchedule}
      >
        {(fullScreenMode) => (
          <>
            <SectionBox
              title='Savy Details'
              onAdd={() => handleAddRow()}
              canEdit={canEditDummySchedule}
            >
              <CommonTable
                rows={scheduleData.rows}
                columns={[
                  {
                    key: "name",
                    label: "Name",
                    headerAlign: "left",
                    render: (row, index) => (
                      <TextFieldForName
                        value={row.name}
                        onChange={(value) => handleChange(index, "name", value)}
                        disabled={isAutoload(row.status)}
                        canEdit={canEditDummySchedule}
                      />
                    ),
                  },
                  {
                    key: "amount",
                    label: "Amount",
                    headerAlign: "right",
                    colAlign: "right",
                    render: (row, index) => (
                      <Tooltip
                        title={
                          row?.historyAmount &&
                          row.historyAmount.length > 0 &&
                          formatHistoryAmount(row.historyAmount)
                        }
                        placement='top'
                      >
                        <Box display='inline-block' sx={{ width: "100%" }}>
                          <CalculatorTextField
                            allowDecimal
                            value={row?.historyAmount || []}
                            onChange={(value) =>
                              handleChange(index, "amount", value)
                            }
                            fullWidth={true}
                            disabled={isAutoload(row.status)}
                            needRound={false}
                            canEdit={canEditDummySchedule}
                          />
                        </Box>
                      </Tooltip>
                    ),
                  },
                  {
                    key: "reference",
                    label: "Reference",
                    headerAlign: "center",
                    render: (row, index) => (
                      <TextFieldForName
                        value={row?.reference}
                        onChange={(value) =>
                          handleChange(index, "reference", value)
                        }
                        canEdit={canEditDummySchedule}
                        viewOnlyAlign='center'
                      />
                    ),
                  },
                  {
                    key: "source",
                    label: "Sch",
                    headerAlign: "center",
                    render: (row) => (
                      <TextFieldForViewOnly
                        value={row.source?.sch_no || ""}
                        sx={{ textAlign: "center" }}
                      />
                    ),
                  },
                  canEditDummySchedule
                    ? {
                        key: "action",
                        label: "Action",
                        headerAlign: "center",
                        render: (row, index) => (
                          <IconButtonWithTooltip
                            icon={DeleteOutlineIcon}
                            tooltipTitle='Delete Row'
                            onClick={() => handleRemoveRow(index)}
                            color='error'
                            size='small'
                            disabled={isAutoload(row.status)}
                          />
                        ),
                      }
                    : [],
                ]}
              >
                <TableCell align='right'>Total Amount</TableCell>
                <TableCell align='right'>
                  {formatAmount(calculateTotalAmount(), false)}
                </TableCell>
                <TableCell colSpan={canEditDummySchedule ? 4 : 3}></TableCell>
              </CommonTable>
            </SectionBox>

            {/* <Divider sx={{ marginY: 4 }} /> */}

            {/* Spreadsheet section */}
            {/* <SpreadsheetComponent
              ref={spreadsheetRef}
              allowSorting={true}
              allowFiltering={true}
              height={600}
              width='100%'
              created={loadData}
              allowPageSetup={canEditDummySchedule}
              allowEditing={canEditDummySchedule}
              enableContextMenu={canEditDummySchedule}
              key={scheduleId} // Force unique reinitialization for each instance
            >
              <SheetsDirective>
                <SheetDirective name='Sheet1'>
                  <RangesDirective>
                    <RangeDirective startCell='A1' />
                  </RangesDirective>
                </SheetDirective>
              </SheetsDirective>
            </SpreadsheetComponent> */}
          </>
        )}
      </ScheduleContainer>

      <Divider sx={{ marginY: 2 }} />

      <ScheduleNotes
        notes={scheduleData.comments}
        setNotes={(notes) => handleFieldChange("comments", notes)}
        reviewNotes={scheduleData.reviewNotes}
        setReviewNotes={(notes) => handleFieldChange("reviewNotes", notes)}
        enableEdit={canEditDummySchedule}
      />

      {/* <CustomDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirmSave}
        title='Save Confirmation'
        description={"Do you want to proceed with saving this Savy Schedule?"}
        type='general'
        loading={loading}
      /> */}

      <SavyExcelUpload
        company={company}
        open={excelDialogOpen}
        handleCloseDialog={handleCloseExcelUploadDialog}
        onExcelAddRows={onExcelAddRows}
      />
    </Box>
  );
};

export default DummySchedule;
